import { useEffect, useState } from 'react'
import api from '../services/api'

// Venda de férias (abono pecuniário): até 10 dias (1/3) de um período aquisitivo; valor = salário ÷ 30 × dias × 4/3.
// Grava em frota.ferias_venda (tabela só do web) e abate dos dias do período no Controle de Férias.

interface Periodo { ini: string; fim: string; limite: string; gozados: number; programados: number; vendidos: number; saldo: number; status: string }
interface Venda { periodoIni: string; dias: number; dataRequerimento: string; salarioBase: number; valor: number; dataPagamento: string; obs: string }
interface Dados { tabelaCriada: boolean; admissao: string; salario: number; periodos: Periodo[]; vendas: Venda[] }
interface Form { periodoIni: string; dias: string; dataRequerimento: string; valor: string; dataPagamento: string; obs: string; novo: boolean }

const dataBr = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const moeda = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const numero = (s: string) => Number(String(s).replace(/\./g, '').replace(',', '.'))
const sugerido = (salario: number, dias: number) => Math.round(salario / 30 * dias * 4 / 3 * 100) / 100
const txt = (n: number) => n.toFixed(2).replace('.', ',')

export default function FuncionarioFeriasVenda({ motcod }: { motcod: string }) {
  const [dados, setDados] = useState<Dados | null>(null)
  const [form, setForm] = useState<Form | null>(null)
  const [valorEditado, setValorEditado] = useState(false)
  const [erro, setErro] = useState('')
  const [avisos, setAvisos] = useState<string[]>([])
  const [ok, setOk] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function carregar() {
    const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}/ferias-venda`)
    setDados(r.data)
  }
  useEffect(() => { carregar().catch(() => setErro('Não foi possível carregar a venda de férias.')) }, [motcod])

  function novaVenda() {
    const livres = dados!.periodos.filter(p => !dados!.vendas.some(v => v.periodoIni === p.ini))
    const p = livres.find(x => x.saldo > 0) ?? livres[0]
    if (!p) { setErro('Todos os períodos já têm venda lançada.'); return }
    setErro(''); setAvisos([]); setOk(''); setValorEditado(false)
    setForm({ periodoIni: p.ini, dias: '10', dataRequerimento: '', valor: txt(sugerido(dados!.salario, 10)), dataPagamento: '', obs: '', novo: true })
  }

  function editar(v: Venda) {
    setErro(''); setAvisos([]); setOk(''); setValorEditado(true)
    setForm({ periodoIni: v.periodoIni, dias: String(v.dias), dataRequerimento: v.dataRequerimento, valor: txt(v.valor), dataPagamento: v.dataPagamento, obs: v.obs, novo: false })
  }

  function mudaDias(d: string) {
    setForm(f => ({ ...f!, dias: d, valor: valorEditado ? f!.valor : txt(sugerido(dados!.salario, Number(d) || 0)) }))
  }

  async function salvar() {
    if (!form) return
    setErro(''); setAvisos([]); setOk(''); setSalvando(true)
    try {
      const r = await api.put(`/funcionarios/${encodeURIComponent(motcod)}/ferias-venda`, {
        periodoIni: form.periodoIni, dias: Number(form.dias), dataRequerimento: form.dataRequerimento,
        valor: numero(form.valor), dataPagamento: form.dataPagamento, obs: form.obs,
      })
      await carregar()
      setAvisos(r.data?.avisos ?? [])
      setOk(r.data?.semAlteracao ? 'Nenhuma alteração para salvar.' : 'Venda de férias salva.')
      setForm(null)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar a venda de férias.')
    } finally {
      setSalvando(false)
    }
  }

  async function excluir(v: Venda) {
    if (!window.confirm(`Excluir a venda de ${v.dias} dias do período ${dataBr(v.periodoIni)}?`)) return
    setErro(''); setAvisos([]); setOk('')
    try {
      await api.delete(`/funcionarios/${encodeURIComponent(motcod)}/ferias-venda/${v.periodoIni}`)
      await carregar()
      setOk('Venda excluída.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao excluir a venda.')
    }
  }

  const th: React.CSSProperties = { textAlign: 'left', padding: '0.45rem 0.6rem', fontSize: '0.72rem' }
  const td: React.CSSProperties = { padding: '0.35rem 0.6rem', fontSize: '0.82rem' }
  const campo: React.CSSProperties = { padding: '0.35rem 0.5rem', fontSize: '0.82rem' }
  const periodoAtual = form && dados?.periodos.find(p => p.ini === form.periodoIni)
  const opcoes = form && dados ? dados.periodos.filter(p => p.ini === form.periodoIni || !dados.vendas.some(v => v.periodoIni === p.ini)) : []

  return (
    <div style={{ marginTop: '1.4rem', paddingTop: '1rem', borderTop: '2px solid #eff6ff' }}>
      <h3 style={{ margin: '0 0 0.2rem', fontSize: '0.95rem', color: '#1a6ea8' }}>Venda de férias (abono pecuniário)</h3>
      <p style={{ margin: '0 0 0.7rem', fontSize: '0.74rem', color: '#6b7280' }}>
        Até 10 dias (1/3) de cada período aquisitivo. Os dias vendidos saem do saldo de férias no Controle de Férias.
      </p>

      {!dados ? (
        <p style={{ color: erro ? '#dc2626' : '#6b7280', fontSize: '0.82rem' }}>{erro || 'Carregando...'}</p>
      ) : !dados.tabelaCriada ? (
        <p style={{ fontSize: '0.8rem', color: '#92400e', background: '#fef3c7', borderRadius: 8, padding: '0.55rem 0.8rem' }}>
          A venda de férias ainda não está habilitada nesta base (falta criar a tabela de venda).
        </p>
      ) : !dados.admissao ? (
        <p style={{ fontSize: '0.8rem', color: '#6b7280' }}>Funcionário sem data de admissão: não há períodos aquisitivos.</p>
      ) : (
        <>
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={th}>Período aquisitivo</th><th style={{ ...th, width: 50 }}>Dias</th><th style={th}>Requerimento</th>
                <th style={th}>Valor</th><th style={th}>Pagamento</th><th style={th}>Observação</th><th style={{ ...th, width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {dados.vendas.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#6b7280', padding: '1rem' }}>Nenhuma venda de férias lançada.</td></tr>
              )}
              {dados.vendas.map(v => {
                const p = dados.periodos.find(x => x.ini === v.periodoIni)
                return (
                  <tr key={v.periodoIni}>
                    <td style={td}>{p ? `${dataBr(p.ini)} a ${dataBr(p.fim)}` : dataBr(v.periodoIni)}</td>
                    <td style={td}>{v.dias}</td>
                    <td style={td}>{dataBr(v.dataRequerimento) || '—'}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{moeda(v.valor)}</td>
                    <td style={td}>{dataBr(v.dataPagamento) || '—'}</td>
                    <td style={td}>{v.obs}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => editar(v)}>Editar</button>{' '}
                      <button className="btn btn-sm" style={{ background: '#dc2626', color: '#fff' }} onClick={() => excluir(v)}>Excluir</button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {!form && (
            <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }} onClick={novaVenda}>+ Nova venda</button>
          )}

          {form && (
            <div style={{ marginTop: '0.9rem', padding: '0.8rem', background: '#f7faff', border: '1px solid #bfdbfe', borderRadius: 8 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.7rem 1rem', alignItems: 'flex-end' }}>
                <div className="field" style={{ margin: 0, width: 290 }}>
                  <label>Período aquisitivo</label>
                  <select value={form.periodoIni} disabled={!form.novo} style={campo} onChange={e => setForm(f => ({ ...f!, periodoIni: e.target.value }))}>
                    {opcoes.map(p => <option key={p.ini} value={p.ini}>{dataBr(p.ini)} a {dataBr(p.fim)} · saldo {p.saldo} d</option>)}
                  </select>
                </div>
                <div className="field" style={{ margin: 0, width: 80 }}>
                  <label>Dias</label>
                  <input type="number" min={1} max={10} value={form.dias} style={campo} onChange={e => mudaDias(e.target.value)} />
                </div>
                <div className="field" style={{ margin: 0, width: 160 }}>
                  <label>Data do requerimento</label>
                  <input type="date" value={form.dataRequerimento} style={campo} onChange={e => setForm(f => ({ ...f!, dataRequerimento: e.target.value }))} />
                </div>
                <div className="field" style={{ margin: 0, width: 130 }}>
                  <label>Valor (R$)</label>
                  <input value={form.valor} inputMode="decimal" style={campo} onChange={e => { setValorEditado(true); setForm(f => ({ ...f!, valor: e.target.value })) }} />
                </div>
                <div className="field" style={{ margin: 0, width: 160 }}>
                  <label>Data do pagamento</label>
                  <input type="date" value={form.dataPagamento} style={campo} onChange={e => setForm(f => ({ ...f!, dataPagamento: e.target.value }))} />
                </div>
                <div className="field" style={{ margin: 0, width: 240 }}>
                  <label>Observação</label>
                  <input value={form.obs} maxLength={100} style={campo} onChange={e => setForm(f => ({ ...f!, obs: e.target.value.toUpperCase() }))} />
                </div>
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.74rem', color: '#6b7280' }}>
                Valor sugerido: {moeda(sugerido(dados.salario, Number(form.dias) || 0))} (salário {moeda(dados.salario)} ÷ 30 × {Number(form.dias) || 0} dias × 4/3, já com o 1/3 de férias).
                {periodoAtual && <> Requerimento até {dataBr(new Date(new Date(`${periodoAtual.fim}T12:00:00`).getTime() - 15 * 86400000).toISOString().slice(0, 10))} (15 dias antes do fim do período).</>}
              </div>
              <div style={{ marginTop: '0.7rem', display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-orange btn-sm" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar venda'}</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setForm(null)} disabled={salvando}>Cancelar</button>
              </div>
            </div>
          )}
        </>
      )}

      {erro && dados && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '0.8rem 0 0' }}>{erro}</p>}
      {avisos.map((a, i) => <p key={i} style={{ color: '#92400e', background: '#fef3c7', borderRadius: 8, padding: '0.4rem 0.7rem', fontSize: '0.78rem', margin: '0.6rem 0 0' }}>⚠ {a}</p>)}
      {ok && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '0.8rem 0 0' }}>{ok}</p>}
    </div>
  )
}
